import { type ReactNode } from "react";
import classNames from "classnames";
import { Stack, styled, type TypographyProps, type StackProps, type SxProps, type Theme } from "~/common/mui";
import { Parameter, type ParameterProps, ParameterStack } from "~/common/components/ParameterV2";
import { mergeSlotProps } from "~/common/utils/muiUtils";

export type LegacyParameterVariant = "vertical" | "horizontal" | "search-page";

export interface ProductParameterBaseProps extends Omit<DivAttributes, "children"> {
  label: ReactNode;
  value?: ReactNode;
  children?: ReactNode;
  variant?: LegacyParameterVariant | ParameterProps["variant"];
  cutOverflow?: boolean;
  reverse?: boolean;
  slotProps?: ParameterProps["slotProps"];
  "data-test-id"?: string;
}

export interface ProductParameterStackProps extends StackProps {
  parameterVariant?: ProductParameterBaseProps["variant"];
  slotProps?: ParameterProps["slotProps"];
}

export const ProductParameter = ({
  label,
  value,
  children,
  variant = "vertical",
  cutOverflow,
  reverse,
  slotProps,
  className,
  "data-test-id": testId,
  ...rest
}: ProductParameterBaseProps) => {
  const effectiveValue = children ?? value;
  const parameterVariant = mapParameterVariant(variant);

  const nextSlotProps: ParameterProps["slotProps"] = {
    label: slotProps?.label,
    value: mergeSlotProps<TypographyProps & { "data-test-id"?: string }>(
      cutOverflow ? CUT_OVERFLOW_SLOT_PROPS : undefined,
      testId ? { "data-test-id": testId } : undefined,
      slotProps?.value,
    ),
  };

  return (
    <div {...rest} className={className}>
      <Parameter
        label={label}
        value={effectiveValue}
        variant={parameterVariant}
        reverse={reverse}
        slotProps={nextSlotProps}
      />
    </div>
  );
};

export const ProductParameterStack = ({
  children,
  parameterVariant,
  slotProps,
  ...rest
}: ProductParameterStackProps) => {
  return (
    <ParameterStack {...rest} parameterVariant={mapParameterVariant(parameterVariant)} slotProps={slotProps}>
      {children}
    </ParameterStack>
  );
};

export const SearchPageParamContainer = ({ className, ...rest }: JSX.IntrinsicElements["div"]) => (
  <div className={classNames("scha__param--search-page__wrapper", className)} {...rest} />
);

export const ParamGroupStack = styled(Stack)({
  alignItems: "flex-start",
});

const CUT_OVERFLOW_SLOT_PROPS = {
  sx: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies { sx: SxProps<Theme> };

function mapParameterVariant(variant: ProductParameterBaseProps["variant"]): ParameterProps["variant"] | undefined {
  if (!variant) {
    return undefined;
  }

  if (variant === "vertical") {
    return "column";
  }

  if (variant === "horizontal" || variant === "search-page") {
    return "row";
  }

  return variant;
}
